/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Mantine
import { Box, Title, Stack } from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './AddMeterPointsForm.module.css';

// Utils
import { convertArrayToLabelValue } from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';
import { useSites } from 'shared/api/get-sites';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import SingleFormMeterPoint from './SingleFormMeterPoint';
import { randomId, useMediaQuery } from '@mantine/hooks';
import { IconCirclePlus } from '@tabler/icons-react';
import { useAddMeterPoint } from '../../../api/create-meter-point';
import { Divider } from '@components/atoms';
import { useProducers } from 'shared/api/get-producers';
import { TMesurabilityProject } from '@features/offsets/types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddMeterPointsForm = (props: {
	form: any;
	onCancel: () => void;
	onBack: () => void;
	isPreviousMeasurabilityProjectDetails: boolean;
}) => {
	const [meterPointsProducer, setMeterPointsProducer] =
		useState('');

	const [countryCodeSite, setCountryCodeSite] =
		useState('');

	const [placeSite, setPlaceSite] = useState('');

	const { meterPointsPage, meterPointsPageSize } =
		useStore();

	const { t } = useTranslation();

	const { data: projects } = useMeasurabilities({
		entity: meterPointsProducer
			? meterPointsProducer
			: [],
	});
	const { data: sites } = useSites();

	const { data: producersToAddProductions } =
		useProducers();

	const addMeterPoint = useAddMeterPoint(
		meterPointsPage,
		meterPointsPageSize
	);
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	useEffect(() => {
		if (addMeterPoint.isSuccess) {
			props.onBack();
		}
	}, [addMeterPoint.isSuccess]);

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			props.form.validate();
			if (props.form.isValid()) {
				addMeterPoint.mutate(props.form.values);
			}
		};

		/* ******************************** CONSTANTS ******************************* */

		const requiredFields = [
			'organization',
			'entity',
			'site',
			'project',
			'startDate',
		];
		const isValidSelects = requiredFields.every((field) =>
			props.form.isValid(field)
		);
		const isEnableToAdd = isValidSelects;

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.form.getInputProps('organization');

		const { onChange: onChangeSite, ...entityProps } =
			props.form.getInputProps('entity');

		const handleChangeEntity = (value: string) => {
			if (value) setMeterPointsProducer(value);
			props.form.setFieldValue('entity', value);
			props.form.setFieldValue('project', null);
		};

		const {
			onChange: onChangeProject2,
			...projectProps
		} = props.form.getInputProps('project');

		const handleChangeProject = (value: string) => {
			props.form.setFieldValue('project', value);
			props.form.setFieldValue('site', null);
		};

		const { onChange: onChangeSite2, ...siteProps } =
			props.form.getInputProps('site');

		const handleChangeSite = (value: string) => {
			props.form.setFieldValue('site', value);
		};

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (props.form.values && props.form.values.site) {
				const siteId = props.form.values.site;
				if (siteId) {
					const foundCountry = sites.find(
						(_site: TMesurabilityProject) =>
							_site.id === siteId
					);
					if (
						foundCountry &&
						foundCountry.location &&
						foundCountry.location.place
					) {
						setPlaceSite(foundCountry.location.place);

						if (
							Array.isArray(
								props.form.getValues().meterPoints
							) &&
							props.form.getValues().meterPoints.length >
								0
						) {
							for (var index in props.form.getValues()
								.meterPoints) {
								props.form.setFieldValue(
									`meterPoints.${index}.location.place`,
									foundCountry.location.place
								);
							}
						}
					}

					if (
						foundCountry &&
						foundCountry.location &&
						foundCountry.location.countryCode
					) {
						setCountryCodeSite(
							foundCountry.location.countryCode
						);
						if (
							Array.isArray(
								props.form.getValues().meterPoints
							) &&
							props.form.getValues().meterPoints.length >
								0
						) {
							for (var index in props.form.getValues()
								.meterPoints) {
								props.form.setFieldValue(
									`meterPoints.${index}.location.countryCode`,
									foundCountry.location.countryCode
								);
							}
						}
					}
				}
			}
		}, []);

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(
					(values: any) => {}
				)}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.meterPoints')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('meterPoints.addMeterPoint')}
					</Text>
				</Box>

				{/* Row for Producer */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								props.isPreviousMeasurabilityProjectDetails
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(
								producersToAddProductions
							)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
				</Box>
				{/* Row for Project & Site */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								!props.form.isValid('entity') ||
								props.isPreviousMeasurabilityProjectDetails
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.project')}
							withAsterisk
							data={convertArrayToLabelValue(projects)}
							placeholder={t('global.selectProject')}
							onChange={handleChangeProject}
							{...projectProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								!props.form.isValid('project') ||
								props.isPreviousMeasurabilityProjectDetails
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.site')}
							withAsterisk
							data={convertArrayToLabelValue(
								sites.filter((item: any) =>
									item.project
										? item.project.id ==
											props.form.getInputProps('project')
												.value
										: []
								)
							)}
							placeholder={t('global.selectSite')}
							onChange={handleChangeSite}
							{...siteProps}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				<Stack>
					{props.form
						.getValues()
						.meterPoints.map(
							(item: any, index: number) => (
								<SingleFormMeterPoint
									key={item.id}
									form={props.form}
									item={item}
									index={index}
								/>
							)
						)}
					<Box>
						<Button
							disabled={
								Array.isArray(
									props.form.getValues().meterPoints
								) &&
								props.form.getValues().meterPoints
									.length > 3
							}
							size="compact-sm"
							color="blue"
							leftSection={<IconCirclePlus size={23} />}
							onClick={() =>
								props.form.insertListItem('meterPoints', {
									location: {
										countryCode: countryCodeSite,
										place: placeSite,
									},
									serialNumber: '',
									id: randomId(),
								})
							}
							variant="transparent"
						>
							{t('meterPoints.addAnotherMeterpoint')}
						</Button>
					</Box>
				</Stack>
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							data-autofocus
							disabled={!isEnableToAdd}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={addMeterPoint.isLoading}
						>
							{t('meterPoints.addMeterPoint')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
